import { useState } from 'react';
import { Form, Input, Button } from 'antd';
import { Check, LockKeyhole } from 'lucide-react';

import { useI18n } from '../../../i18n';
import { getErrorCode, getErrorMessage } from '../../../utils';

const RULES = [
  { key: 'ruleLength', test: v => v.length >= 8 },
  { key: 'ruleLetter', test: v => /[a-zA-Z]/.test(v) },
  { key: 'ruleDigit', test: v => /\d/.test(v) },
];

export const NewPasswordForm = ({ onSubmit, isLoading }) => {
  const { t } = useI18n();
  const [form] = Form.useForm();
  const password = Form.useWatch('password', form) || '';
  const [showRuleErrors, setShowRuleErrors] = useState(false);
  const isStrong = RULES.every(rule => rule.test(password));

  const onFinish = async values => {
    if (!isStrong) {
      setShowRuleErrors(true);
      return;
    }
    try {
      await onSubmit(values.password);
    } catch (err) {
      if (getErrorCode(err) === 'password_weak') {
        setShowRuleErrors(true);
        form.setFields([{ name: 'password', errors: [getErrorMessage(err)] }]);
      }
    }
  };

  return (
    <Form
      form={form}
      name='new-password'
      layout='vertical'
      requiredMark={false}
      onFinish={onFinish}
      onFinishFailed={() => setShowRuleErrors(true)}
    >
      <Form.Item
        label={t('resetPassword.password')}
        name='password'
        style={{ marginBottom: 12 }}
        validateStatus={showRuleErrors && !isStrong ? 'error' : undefined}
      >
        <Input.Password
          size='large'
          prefix={<LockKeyhole size={16} className='wm-muted' />}
          placeholder={t('resetPassword.passwordPlaceholder')}
          autoComplete='new-password'
          autoFocus
        />
      </Form.Item>
      <ul className='wm-auth__rules'>
        {RULES.map(rule => (
          <li
            key={rule.key}
            className={rule.test(password) ? 'is-valid' : showRuleErrors ? 'is-invalid' : undefined}
          >
            <Check size={14} />
            {t(`resetPassword.${rule.key}`)}
          </li>
        ))}
      </ul>
      <Form.Item
        label={t('resetPassword.confirm')}
        name='confirm'
        dependencies={['password']}
        rules={[
          { required: true, message: t('resetPassword.confirmRequired') },
          ({ getFieldValue }) => ({
            validator: (_, value) =>
              !value || getFieldValue('password') === value
                ? Promise.resolve()
                : Promise.reject(new Error(t('resetPassword.mismatch'))),
          }),
        ]}
      >
        <Input.Password
          size='large'
          prefix={<LockKeyhole size={16} className='wm-muted' />}
          placeholder={t('resetPassword.confirmPlaceholder')}
          autoComplete='new-password'
        />
      </Form.Item>
      <Button type='primary' htmlType='submit' loading={isLoading} block style={{ marginTop: 8 }}>
        {t('resetPassword.submit')}
      </Button>
    </Form>
  );
};
